import { createAsyncThunk } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

import { BFF_BASE_URL } from '@/requests';
import { AnalyticsService, EVENTS, log } from '@/services';
import {
  formatTrackerExportFileName,
  transformTrackerDataToCsv,
} from '@/utils';

import { selectUserCompliance } from '../player-fsm-api';
import { selectAccessToken } from '../session';
import { RootState } from '../store';
import { trackerPostgrestApi } from '../tracker-pg-api';

export const downloadCertificate = createAsyncThunk<
  void,
  { certificateId: number },
  {
    state: RootState;
  }
>('tracker/downloadCertificate', async ({ certificateId }, { getState }) => {
  try {
    AnalyticsService.logEvent(
      EVENTS.TRACKER_EVENTS.DOWNLOAD_CERTIFICATE.INTENT,
      { certificateId },
    );
    const accessToken = selectAccessToken(getState());
    const response = await fetch(
      `${BFF_BASE_URL}/tracker/download-certificate?certificate_id=${certificateId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Error fetching certificate: ${response.statusText}`);
    }
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename =
      contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] ||
      'certificate.pdf';
    saveAs(blob, filename);
    AnalyticsService.logEvent(
      EVENTS.TRACKER_EVENTS.DOWNLOAD_CERTIFICATE.SUCCESS,
      { certificateId },
    );
  } catch (error) {
    AnalyticsService.logEvent(EVENTS.TRACKER_EVENTS.DOWNLOAD_CERTIFICATE.FAIL, {
      certificateId,
      reason: (error as Error).message || 'Unknown error',
    });
    log(error);
    throw error;
  }
});

export const downloadCertificateSummary = createAsyncThunk<
  void,
  { fromDate: string; toDate?: string },
  {
    state: RootState;
  }
>(
  'tracker/downloadCertificateSummary',
  async ({ fromDate, toDate }, { getState, dispatch }) => {
    try {
      const accessToken = selectAccessToken(getState());
      const compliance = selectUserCompliance(getState());
      const credits = await dispatch(
        trackerPostgrestApi.endpoints.fetchCredits.initiate({
          dateRange: {
            fromDate,
            toDate,
          },
        }),
      ).unwrap();
      const zip = new JSZip();
      const folder = zip.folder('certificates');
      const certificateIds = credits
        .filter(credit => !!credit.fileUri)
        .map(credit => credit.certificateId);

      const rowData = transformTrackerDataToCsv(credits, compliance);

      const csvContent = rowData.reduce(
        (content, rowArray) => content + rowArray.join(',') + '\r\n',
        '',
      );

      zip.file('certificate_summary.csv', csvContent);
      const requests = certificateIds.map(async certificateId => {
        const response = await fetch(
          `${BFF_BASE_URL}/tracker/download-certificate?certificate_id=${certificateId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (response.ok) {
          const blob = await response.blob();
          const contentDisposition = response.headers.get(
            'Content-Disposition',
          );
          const filename =
            contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] ||
            `certificate_${certificateId}.pdf`;
          folder?.file(filename, blob);
          return {
            filename,
            blob,
          };
        }
      });

      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.DOWNLOAD_CERTIFICATE_SUMMARY.INTENT,
        {
          dateRange: {
            fromDate,
            toDate,
          },
        },
      );
      await Promise.all(requests);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(
        zipBlob,
        formatTrackerExportFileName({
          fromDate,
          toDate: toDate ?? new Date().toISOString(),
          extension: 'zip',
        }),
      );
      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.DOWNLOAD_CERTIFICATE_SUMMARY.SUCCESS,
      );
    } catch (error) {
      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.DOWNLOAD_CERTIFICATE_SUMMARY.FAIL,
        { reason: (error as Error).message || 'Unknown error' },
      );
      log(error);
      throw error;
    }
  },
);
